import { defaultLocalStorageMap } from './defaultLocalStorageMap';
const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION;
const VERSION_KEY = 'app_version';

function validateColumnSizing(saved: Record<string, any>, defaults: Record<string, any>): Record<string, any> {
    const result = { ...saved };

    for (const key of Object.keys(saved)) {
        const savedCol = saved[key];
        const defaultCol = defaults[key];

        if (defaultCol && typeof savedCol?.minWidth === 'number' && typeof defaultCol.minWidth === 'number') {
            if (savedCol.minWidth > defaultCol.minWidth) {
                result[key] = {
                    ...savedCol,
                    minWidth: defaultCol.minWidth,
                };
            }
        }
    }

    return result;
}

export function deepSyncWithDefaults<T>(stored: any, defaults: T): T {
    const result: any = Array.isArray(defaults) ? [] : {};
    for (const key in defaults) {
        if (stored && key in stored && typeof stored[key] === typeof stored[key]) {
            if (key === 'columnsSizing') validateColumnSizing(stored[key], stored[key]);
            if (typeof defaults[key] === 'object' && defaults[key] !== null && !Array.isArray(defaults[key])) {
                result[key] = deepSyncWithDefaults(stored[key], defaults[key]);
            } else {
                result[key] = stored[key];
            }
        } else {
            result[key] = defaults[key];
        }
    }

    return result;
}

export function deepSync<T>(stored: any, defaults: T): T {
    const result: any = Array.isArray(defaults) ? [] : {};

    for (const key in defaults) {
        const defaultVal = defaults[key];
        const storedVal = stored?.[key];
        if (key === 'columnsSizing') validateColumnSizing(stored[key], stored[key]);
        if (typeof defaultVal === 'object' && defaultVal !== null && !Array.isArray(defaultVal)) {
            result[key] = deepSync(storedVal ?? {}, defaultVal);
        }
        if ((Array.isArray(defaultVal) && Array.isArray(storedVal)) || typeof storedVal === typeof defaultVal) {
            result[key] = defaultVal;
        }
    }

    return result;
}

export function validateAndSyncLocalStorage() {
    if (typeof window === 'undefined') return;

    const storedVersion = localStorage.getItem(VERSION_KEY);

    const shouldUpdate = storedVersion !== APP_VERSION || !storedVersion;

    if (shouldUpdate) {
        console.log(`[LocalStorage] Version changed: ${storedVersion} → ${APP_VERSION}`);
    }

    for (const key in defaultLocalStorageMap) {
        const defaults = defaultLocalStorageMap[key];
        try {
            const raw = localStorage.getItem(key);

            const parsed = raw ? JSON.parse(raw) : undefined;
            const synced = deepSync(parsed, defaults);

            localStorage.setItem(key, JSON.stringify(synced));
        } catch {
            localStorage.setItem(key, JSON.stringify(defaults));
        }
    }

    localStorage.setItem(VERSION_KEY, APP_VERSION || '');
}
